import * as Sentry from '@sentry/node'
import * as SentryIntegrations from '@sentry/integrations'
import getConfig from 'next/config'

/**
 * @description Initializes Sentry with the DSN, release =ID and extra config.
 * This function is modified from https://github.com/zeit/next.js/blob/canary/examples/with-sentry/utils/sentry.js
 * Modified to get vaiables from next's getCofig rather than directly from node.
 * @returns {Object} Object containing intialized sentry object and configured captureException.
 * @returns {Object} Sentry; the initalized Sentry Object.
 * @returns {Function} captureException; a configured captureException.
 */

export default function getSentry() {
  const { publicRuntimeConfig } = getConfig()
  const { NODE_ENV, CGC_WEB_RELEASE_ID, CGC_WEB_SENTRY_DSN } = publicRuntimeConfig

  const sentryOptions = {
    dsn: CGC_WEB_SENTRY_DSN,
    release: CGC_WEB_RELEASE_ID,
    maxBreadcrumbs: 50,
    attachStacktrace: true,
    environment: NODE_ENV,
  }

  sentryOptions.integrations = [new SentryIntegrations.ExtraErrorData()]

  if (NODE_ENV !== 'production' && NODE_ENV !== 'staging') {
    // Don't send errors when in dev mode.
    sentryOptions.beforeSend = () => null

    // Instead, dump the errors to the console
    sentryOptions.integrations = [
      new SentryIntegrations.Debug({
        // Trigger DevTools debugger instead of using console.log
        debugger: false,
      }),
    ]
  }

  Sentry.init(sentryOptions)

  return {
    Sentry,
    captureException: (err, ctx) => {
      Sentry.configureScope((scope) => {
        if (err.message) {
          // De-duplication currently doesn't work correctly for SSR / browser errors
          // so we force deduplication by error message if it is present
          scope.setFingerprint([err.message])
        }

        if (err.statusCode) {
          scope.setExtra('statusCode', err.statusCode)
        }

        if (ctx) {
          const { req, res, errorInfo, query, pathname } = ctx

          if (res && res.statusCode) {
            scope.setExtra('statusCode', res.statusCode)
          }

          if (typeof window !== 'undefined') {
            scope.setTag('ssr', false)
            scope.setExtra('query', query)
            scope.setExtra('pathname', pathname)
          } else {
            scope.setTag('ssr', true)
            scope.setExtra('url', req.url)
            scope.setExtra('method', req.method)
            scope.setExtra('headers', req.headers)
            scope.setExtra('params', req.params)
            scope.setExtra('query', req.query)
          }

          if (errorInfo) {
            Object.keys(errorInfo).forEach((key) => scope.setExtra(key, errorInfo[key]))
          }
        }
      })

      return Sentry.captureException(err)
    },
  }
}
